import { resolveFontSizePx } from '#shared';

export const LABEL_FONT_MIN = 10;
export const LABEL_FONT_MAX = 14;
export const CHAR_WIDTH_FACTOR = 0.6; // monospaced JetBrains Mono works well with 0.6

export const resolveLabelFontPx = () =>
  resolveFontSizePx(LABEL_FONT_MIN, LABEL_FONT_MAX, window.innerWidth);

export const COLLISION_CONFIG = {
  LABEL: {
    padding: 0,
    strength: 1,
    charge: -0.2,
    maxDistance: 70,
    anchorStrength: 1,
  },
  DOT: {
    padding: 0,
    radius: 5,
  },
  POINTER: {
    padding: 10,
    radius: 10,
  },
  STACK: {
    maxSize: 3,
    gap: 2,
    horizontalOffset: 20,
    preferredDirection: 'up' as 'up' | 'down',
  },
};

/**
 * Measure label bounding box for collision/layout.
 * Accepts either a single string or an array of parts (blocks).
 * When passing parts, provide `gap` in pixels between blocks.
 *
 * @param label - Label text as string or array of parts
 * @param gap - Gap in pixels between parts (if label is an array)
 * @param hasTrial - If true, adds trial badge width to total width
 * @param trialBadgePadding - Padding in pixels badge and label
 */
export const measureLabelBox = (
  label: string | string[],
  gap = 0,
  hasTrial = false,
  trialBadgePadding = 0,
) => {
  const fontPx = resolveLabelFontPx();
  const charW = fontPx * CHAR_WIDTH_FACTOR;

  let rawW = 0;
  if (Array.isArray(label)) {
    const partsWidth = label.reduce(
      (acc, part) => acc + part.length * charW,
      0,
    );
    const gapsWidth = Math.max(0, label.length - 1) * gap;
    rawW = partsWidth + gapsWidth;
  } else {
    rawW = label.length * charW;
  }

  // Add trial badge width (icon + gap)
  if (hasTrial) {
    /* Badge scaled to 80% of font size to match visual height of JetBrains Mono */
    const badgeSize = fontPx * 0.8;
    rawW += badgeSize + trialBadgePadding;
  }

  const rawH = fontPx + 3;
  const pad = COLLISION_CONFIG.LABEL.padding;
  const width = Math.round(rawW + pad * 2);
  const height = Math.round(rawH + pad * 2);

  const radius = Math.sqrt(width * width + height * height) / 2 + 0.5;
  return { fontPx, width, height, radius };
};
